import { ApplicationTitle } from './../data/models/application-title.model';
import { UserCategoryType } from './../data/models/user-category-type.model';
import { BaseEntityUtils } from './BaseEntityUtils';
import { User } from '../data/models/user.model';
import { UserRole } from '../data/models/user-role.model';

const USER_TYPE_PROVIDER_BC = 'TRANSPORTATIONPROVIDER';

const APP_TITLE_TP = 'TP';

class UserUtils extends BaseEntityUtils {
  private getEmail(user: User | null | undefined): string | '' {
    return user?.emailAddress || '';
  }

  private getProviderId(user: User | null | undefined): number | '' {
    if (user?.transportationProvider) {
      return user.transportationProvider.transportationProviderID;
    }
    return '';
  }

  private getProviderName(user: User | null | undefined): string {
    if (user?.transportationProvider) {
      return user.transportationProvider.transportationProviderName;
    }
    return '';
  }

  private readonly ROLE_VAR = 'UserRoleID_';

  private getDefaultRoles(
    user: User | null | undefined,
    userRoles: UserRole[],
  ): any {
    const categoryRoles = [
      ...(user?.userCategoryInternals || []),
      ...(user?.userCategoryServiceAccounts || []),
      ...(user?.userCategoryTransportationProviders || []),
    ];
    const roles: { [key: string]: boolean } = {};
    userRoles.forEach((ur) => {
      const hasRole =
        categoryRoles.findIndex((cr) => cr.userRoleID === ur.userRoleID) >= 0;
      roles[this.ROLE_VAR + ur.userRoleID] = hasRole || ur.isDefault;
    });
    return roles;
  }

  public getTypeId(userTypes: UserCategoryType[]): number {
    const uType = userTypes?.find(
      (t) => t.userCategoryTypeBC === USER_TYPE_PROVIDER_BC,
    );
    return uType?.userCategoryTypeID || 0;
  }

  public getApplicationId(applicationTitles: ApplicationTitle[]): number {
    const appTitle = applicationTitles.find(
      (at) => at.applicationTitleBC === APP_TITLE_TP,
    );
    return appTitle?.applicationTitleID || 0;
  }

  public getDefaults(
    user: User | null | undefined,
    userRoles: UserRole[],
  ): any {
    const roles = this.getDefaultRoles(user, userRoles);
    const defaults: { [key: string]: any } = {
      FirstName: this.getDefaultString(user?.userFirstName),
      MiddleName: this.getDefaultString(user?.userMiddleName || ''),
      LastName: this.getDefaultString(user?.userLastName),
      Email: this.getEmail(user),
      IsActive: this.getDefaultBoolean(
        user ? user?.userAccountState == 'active' : true,
      ),
      ProviderID: this.getProviderId(user),
      ProviderName: this.getProviderName(user),
      Password: '',
      ConfirmPassword: '',
      ...roles,
    };
    return defaults;
  }

  private getUserRoles(formData: any): number[] {
    const roleIds: number[] = [];
    let userRoleId: number = 0;
    Object.keys(formData).forEach((key) => {
      if (key.startsWith(this.ROLE_VAR)) {
        const s = key.split('_');
        const id = parseInt(s[1], 10);
        if (s[2] === 'user') {
          userRoleId = id;
        }
        if (formData[key]) {
          roleIds.push(id);
        }
      }
    });
    if (roleIds.length === 0 && userRoleId) {
      roleIds.push(userRoleId);
    }
    return roleIds;
  }

  public getUserFromFormData(
    id: number | undefined,
    providerId: number,
    formData: any,
    typeId: number,
  ): User {
    const userRoleIDs = this.getUserRoles(formData);
    const user: User = {
      userAccountID: id,
      userFirstName: formData.FirstName,
      userMiddleName: formData.MiddleName || null,
      userLastName: formData.LastName,
      emailAddress: formData.Email,
      userCategoryTypeID: typeId,
      applicationGlobalSetting: formData.applicationGlobalSetting,
      transportationProviderID: providerId,
      transportationProvider: {
        transportationProviderID: providerId,
        transportationProviderName: formData.ProviderName,
        transportationProviderTypeID: 0,
        accountNumber: '',
      },
      userAccountState: formData.IsActive ? 'active' : 'inactive',
      userRoleIDs,
      agreedToEULA: false,
      agreedToExternalSupportAgreement: false,
    };
    return user;
  }
}

const userUtils = new UserUtils();

export default userUtils;
